import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import * as XLSX from 'xlsx';

/**
 * Service for exporting data to Excel files
 */
@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {
  constructor() {}

  /**
   * Export field schemas to Excel file with 4 columns: Field Name, External Key, Prompt, Additional Prompt
   * @param documentModel - Name of the document model
   * @param fields - Array of field objects with schema information
   * @returns Observable that completes when file is downloaded
   */
  exportFieldSchemasToExcel(documentModel: string, fields: any[]): Observable<void> {
    return from(this.generateAndDownloadExcel(documentModel, fields)).pipe(
      map(() => undefined),
      catchError((error) => {
        return throwError(() => new Error('Failed to export prompts to Excel. Please try again.'));
      })
    );
  }

  /**
   * Convert extraction schema from API response to Excel file
   * @param documentModel - Name of the document model
   * @param extractionSchema - Extraction schema object from API (with properties field)
   * @param filename - Optional custom filename (with extension)
   * @returns Observable that completes when file is downloaded
   */
  convertExtractionSchemaToExcel(documentModel: string, extractionSchema: any, filename?: string): Observable<void> {
    return from(this.generateExcelFromExtractionSchema(documentModel, extractionSchema, filename)).pipe(
      map(() => undefined),
      catchError((error) => {
        return throwError(() => new Error('Failed to export schema to Excel. Please try again.'));
      })
    );
  }

  /**
   * Generate Excel file and trigger download
   */
  private async generateAndDownloadExcel(documentModel: string, fields: any[]): Promise<void> {
    if (!fields || fields.length === 0) {
      throw new Error('No fields to export');
    }

    // Transform fields to Excel rows
    const excelData = this.transformFieldsToExcelData(fields);

    // Create worksheet from data
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    // Apply formatting
    this.applyWorksheetFormatting(worksheet, excelData.length);

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Field Schemas');

    // Generate filename with timestamp
    const filename = this.generateFilename(documentModel);

    // Write and download file
    XLSX.writeFile(workbook, filename);
  }

  /**
   * Transform field objects to Excel data array (array of arrays)
   * Row structure: [Field Name, External Key, Prompt, Additional Prompt]
   */
  private transformFieldsToExcelData(fields: any[]): any[][] {
    // Header row
    const headers = ['Field Name', 'External Key', 'Prompt', 'Additional Prompt'];
    const data: any[][] = [headers];

    // Data rows
    for (const field of fields) {
      const fieldName = field.fieldName || '';
      const externalKey = this.extractExternalKey(field);
      const { prompt, additionalPrompt } = this.extractPrompts(field);

      data.push([fieldName, externalKey, prompt, additionalPrompt]);
    }

    return data;
  }

  /**
   * Extract external key from field
   */
  private extractExternalKey(field: any): string {
    return field.externalKey?.trim() || '';
  }

  /**
   * Extract prompt and additional prompt based on field type
   */
  private extractPrompts(field: any): { prompt: string; additionalPrompt: string } {
    const fieldType = field.fieldType || 'Simple';

    switch (fieldType) {
      case 'Simple':
        // Simple fields: only prompt, no additional prompt
        return {
          prompt: field.prompt?.trim() || '',
          additionalPrompt: ''
        };

      case 'Array':
        // Array fields: prompt goes to "Prompt", description goes to "Additional Prompt"
        // If both exist, put items.description in Prompt and description in Additional Prompt
        return {
          prompt: field.prompt?.trim() || '',
          additionalPrompt: field.additionalPrompt?.trim() || ''
        };

      case 'Complex':
        // Complex fields: try to extract description from field schema JSON
        return this.extractPromptsFromComplexField(field);

      default:
        return {
          prompt: field.prompt?.trim() || '',
          additionalPrompt: ''
        };
    }
  }

  /**
   * Extract prompts from Complex field by parsing field schema JSON
   */
  private extractPromptsFromComplexField(field: any): { prompt: string; additionalPrompt: string } {
    try {
      const schema = field.fieldSchema ? JSON.parse(field.fieldSchema) : null;

      if (!schema) {
        return {
          prompt: '(Complex schema - see UI for details)',
          additionalPrompt: ''
        };
      }

      // Try to extract description from schema
      let prompt = '';
      let additionalPrompt = '';

      // Check for items.description (array-type complex fields)
      if (schema.items && schema.items.description) {
        prompt = schema.items.description.trim();
      }

      // Check for top-level description
      if (schema.description) {
        if (prompt) {
          // If we already have items.description, put top-level description in additional
          additionalPrompt = schema.description.trim();
        } else {
          // Otherwise, put it in prompt
          prompt = schema.description.trim();
        }
      }

      // Fallback if no description found
      if (!prompt && !additionalPrompt) {
        prompt = '(Complex schema - see UI for details)';
      }

      return { prompt, additionalPrompt };
    } catch (error) {
      return {
        prompt: '(Complex schema - see UI for details)',
        additionalPrompt: ''
      };
    }
  }

  /**
   * Apply formatting to worksheet
   * - Bold header row
   * - Background color for header
   * - Freeze header row
   * - Auto-fit column widths
   * - Enable word wrap for prompt columns
   */
  private applyWorksheetFormatting(worksheet: XLSX.WorkSheet, rowCount: number): void {
    // Set column widths (auto-fit with min/max bounds)
    const columnWidths = [
      { wch: 30 },  // Field Name
      { wch: 25 },  // External Key
      { wch: 50 },  // Prompt
      { wch: 50 }   // Additional Prompt
    ];
    worksheet['!cols'] = columnWidths;

    // Freeze header row
    worksheet['!freeze'] = { xSplit: 0, ySplit: 1 };

    // Apply header row formatting (bold, background color)
    const headerCells = ['A1', 'B1', 'C1', 'D1'];
    for (const cellAddress of headerCells) {
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = {
          font: { bold: true, sz: 11 },
          fill: { fgColor: { rgb: 'F1F5F9' } },
          alignment: { vertical: 'center', horizontal: 'left', wrapText: false }
        };
      }
    }

    // Apply word wrap to prompt columns (C and D) for all data rows
    for (let row = 2; row <= rowCount; row++) {
      const promptCell = `C${row}`;
      const additionalPromptCell = `D${row}`;

      if (worksheet[promptCell]) {
        worksheet[promptCell].s = {
          alignment: { vertical: 'top', horizontal: 'left', wrapText: true }
        };
      }

      if (worksheet[additionalPromptCell]) {
        worksheet[additionalPromptCell].s = {
          alignment: { vertical: 'top', horizontal: 'left', wrapText: true }
        };
      }
    }
  }

  /**
   * Generate filename with document model name and timestamp
   * Format: {documentModel}_{timestamp}.xlsx
   * Example: appraisal_report_1004_20260526_143022.xlsx
   */
  private generateFilename(documentModel: string): string {
    const now = new Date();
    const timestamp = now.getFullYear() +
      String(now.getMonth() + 1).padStart(2, '0') +
      String(now.getDate()).padStart(2, '0') + '_' +
      String(now.getHours()).padStart(2, '0') +
      String(now.getMinutes()).padStart(2, '0') +
      String(now.getSeconds()).padStart(2, '0');
    
    return `${documentModel}_${timestamp}.xlsx`;
  }

  /**
   * Generate Excel file from extraction schema and trigger download
   */
  private async generateExcelFromExtractionSchema(documentModel: string, extractionSchema: any, filename?: string): Promise<void> {
    if (!extractionSchema || !extractionSchema.properties) {
      throw new Error('Invalid extraction schema: missing properties field');
    }

    const properties = extractionSchema.properties;
    const fieldNames = Object.keys(properties);

    if (fieldNames.length === 0) {
      throw new Error('No fields found in extraction schema');
    }

    // Transform extraction schema to Excel rows
    const excelData = this.transformExtractionSchemaToExcelData(properties);

    // Create worksheet from data
    const worksheet = XLSX.utils.aoa_to_sheet(excelData);

    // Apply formatting
    this.applyWorksheetFormatting(worksheet, excelData.length);

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Field Schemas');

    // Use provided filename or generate default
    const finalFilename = filename || this.generateFilename(documentModel);

    // Write and download file
    XLSX.writeFile(workbook, finalFilename);
  }

  /**
   * Transform extraction schema properties to Excel data array
   * Row structure: [Field Name, External Key, Prompt, Additional Prompt]
   * 
   * Mapping logic:
   * - Field Name: property key
   * - External Key: external_key field or field name if missing
   * - Prompt: items.description (for arrays) OR description (for simple fields)
   * - Additional Prompt: field-level description (for arrays only)
   */
  private transformExtractionSchemaToExcelData(properties: any): any[][] {
    // Header row
    const headers = ['Field Name', 'External Key', 'Prompt', 'Additional Prompt'];
    const data: any[][] = [headers];

    // Data rows
    for (const fieldName of Object.keys(properties)) {
      const fieldSchema = properties[fieldName];
      
      // Extract external key
      const externalKey = fieldSchema.external_key;
      
      // Extract prompt and additional prompt based on field type
      let prompt = '';
      let additionalPrompt = '';
      
      if (fieldSchema.type === 'array' && fieldSchema.items?.description) {
        // Array field: items.description → Prompt, field description → Additional Prompt
        prompt = fieldSchema.items.description.trim();
        additionalPrompt = fieldSchema.description ? fieldSchema.description.trim() : '';
      } else if (fieldSchema.description) {
        // Simple field: description → Prompt
        prompt = fieldSchema.description.trim();
        additionalPrompt = '';
      }

      data.push([fieldName, externalKey, prompt, additionalPrompt]);
    }

    return data;
  }
}
